import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { toast } from "sonner";

import { ONE_GB, ONE_KB, TEN, allowFileTypesMIME } from "@/constants/constants";
import { cn } from "@/lib/utils";
import Icons from "@/utils/Icons";

type FileDropProps = {
  files: File[] | string;
  setFiles: (files: File[]) => void;
  image?: string;
  type?: "input" | "box" | "imageBox";
  withCropper?: boolean;
  strictCrop?: boolean;
  aspect?: number;
  fileName?: string;
  placeholder?: string;
  maxSize?: number;
  onDropRejected?: (rejectedFiles: FileRejection[]) => void;
  onFileDialogOpen?: () => void;
  resetDragState?: boolean;
};

type DragStyles = {
  border: string;
  bg: string;
  ring: string;
  text: string;
  iconColor: string;
};

const FileDropZone: React.FC<FileDropProps> = ({
  files,
  setFiles,
  type = "box",
  withCropper,
  maxSize = TEN * ONE_GB,
  onDropRejected,
  onFileDialogOpen,
  resetDragState = false,
}) => {
  const [, setCurrentFiles] = useState<File[]>([]);
  const [dragState, setDragState] = useState<"inactive" | "active">("inactive");
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const processFiles = useCallback(
    (acceptedFiles: File[]): void => {
      if (acceptedFiles.length === 0) {
        return;
      }
      // File processing
      setFiles(acceptedFiles);
      setCurrentFiles(acceptedFiles);
    },
    [setFiles],
  );

  const handleFileDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setDragState("inactive");

      if (rejectedFiles.length > 0) {
        if (onDropRejected) {
          onDropRejected(rejectedFiles);
        } else {
          const tooLarge = rejectedFiles.some(
            (rejection) => rejection.file.size > maxSize,
          );

          const errorMessage = tooLarge
            ? `File size should not exceed ${(maxSize / (ONE_KB * ONE_KB)).toFixed(1)}MB`
            : "Invalid file format. Please upload a valid file";

          toast.error(errorMessage);
        }

        return;
      }

      await processFiles(acceptedFiles);
    },
    [maxSize, onDropRejected, processFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: allowFileTypesMIME
      ? allowFileTypesMIME
          .split(",")
          .reduce((acc: Record<string, string[]>, type) => {
            const trimmed = type.trim();
            if (trimmed.startsWith(".")) {
              acc[trimmed] = [];
            } else if (trimmed.includes("/")) {
              acc[trimmed] = [];
            }

            return acc;
          }, {})
      : undefined,
    maxSize,
    onDrop: handleFileDrop,
    onFileDialogOpen,
    noClick: false,
    noKeyboard: false,
    multiple: true,
  });

  // Check if point is inside element
  const isPointInsideElement = (
    x: number,
    y: number,
    element: HTMLElement,
  ): boolean => {
    const rect = element.getBoundingClientRect();

    return (
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
    );
  };

  // Custom drag handlers to only respond when directly over the component
  useEffect(() => {
    const handleDragOver = (e: DragEvent): void => {
      e.preventDefault();

      if (dropzoneRef.current && e.dataTransfer?.types.includes("Files")) {
        // Check if drag is directly over our component
        if (isPointInsideElement(e.clientX, e.clientY, dropzoneRef.current)) {
          setDragState("active");
        }
      }
    };

    const handleDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      if (dropzoneRef.current) {
        const isLeavingDropzone = !dropzoneRef.current.contains(
          e.relatedTarget as Node,
        );

        if (isLeavingDropzone) {
          setDragState("inactive");
        }
      }
    };

    const handleDrop = (): void => {
      setDragState("inactive");
    };

    const handleDragEnd = (): void => {
      setDragState("inactive");
    };

    // Add listeners to document to catch global events
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("drop", handleDrop);
    document.addEventListener("dragend", handleDragEnd);

    // Clean up
    return (): void => {
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("drop", handleDrop);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  useEffect(() => {
    if (!withCropper) {
      if (typeof files !== "string" && files?.length) {
        setCurrentFiles(
          files?.map((file) =>
            Object.assign(file, {
              preview: URL?.createObjectURL(file),
            }),
          ),
        );
      }
    }
  }, [files, withCropper]);

  // Reset drag state when resetDragState prop changes
  useEffect(() => {
    if (resetDragState) {
      setDragState("inactive");
    }
  }, [resetDragState]);

  // Component type renders
  if (type === "input") {
    return (
      <div {...getRootProps()} ref={dropzoneRef}>
        <input {...getInputProps()} />
      </div>
    );
  }

  // Default box type
  const getDragStyles = (): DragStyles => ({
    border:
      dragState === "active"
        ? "border-blue-400 border-dashed"
        : "border-[#D8D6DE] dashed-border",
    bg: dragState === "active" ? "bg-blue-50" : "bg-[#F8F8F8]",
    ring: dragState === "active" ? "ring-4 ring-blue-400" : "",
    text: dragState === "active" ? "text-blue-500" : "text-[#534F67]",
    iconColor: dragState === "active" ? "#3b82f6" : "#667085",
  });

  const styles = getDragStyles();

  return (
    <Fragment>
      <div
        {...getRootProps()}
        className={cn("transition-all duration-200", styles.ring)}
        ref={dropzoneRef}
      >
        <div
          className={cn(
            "py-4 rounded-md border-2 transition-all duration-200",
            styles.bg,
            styles.border,
          )}
        >
          <input {...getInputProps()} />
          <div className='flex flex-col justify-center items-center cursor-pointer py-3'>
            <Icons name='cloud-icon' color={styles.iconColor} size={28} />
            <h2 className={cn("font-normal text-base", styles.text)}>
              {dragState === "active" ? "Drop files here" : " "}
            </h2>
            <p className='font-normal text-sm text-gray-400 mt-1'>
              {dragState === "active"
                ? "Release to upload"
                : `Drop files or folder here or click `}
              <span className='text-blue-600'>browse</span> through your device
            </p>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default FileDropZone;
